import { Actions, createEffect, ofType } from "@ngrx/effects";
import {
  InstructionActions,
  InstructionDetailsActions,
  NotificationActions,
} from "../actions";
import {
  catchError,
  filter,
  map,
  mergeMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { Injectable } from "@angular/core";
import { InstructionService } from "src/app/services/instruction.service";
import { of } from "rxjs";
import { ChapterService } from "src/app/services/chapter.service";
import { AppState } from "../reducers";
import { Store } from "@ngrx/store";
import { InstructionDetailsSelectors } from "../selectors";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class InstructionDetailsEffects {
  loadMyInstruction$ = createEffect(() =>
    this.actions.pipe(
      ofType(InstructionDetailsActions.loadMyInstruction),
      mergeMap((action) =>
        this.instructionService.loadMyInstruction(action.name).pipe(
          map((instruction) =>
            InstructionDetailsActions.loadedMyInstruction({
              instruction,
            }),
          ),
        ),
      ),
    ),
  );

  // This is triggered when the instruction is opened! - This has nothing to do with the actual "read" as we just trigger "read" as soon as the user opens the instruction.
  // This is defined in: https://github.com/SAR-Anlagenbau/api/issues/66#issue-3729385112
  // looks wrong but is expected behavior!
  triggerMarkInstructionConfirmationAsReadWhenMyInstructionConfirmationIsLoaded$ =
    createEffect(
      () =>
        this.actions.pipe(
          ofType(InstructionDetailsActions.loadedMyInstructionConfirmation),
          filter(
            (result) =>
              result.confirmation != undefined &&
              result.confirmation.read == false,
          ),
          mergeMap((action) =>
            this.instructionService.markAsRead(action.confirmation!.name),
          ),
          tap((_) => console.log(_)),
        ),
      {
        dispatch: false,
      },
    );

  triggerLoadInstructionConfirmationWhenMyInstructionIsLoaded$ = createEffect(
    () =>
      this.actions.pipe(
        ofType(InstructionDetailsActions.loadMyInstruction),
        map((action) =>
          InstructionDetailsActions.loadMyInstructionConfirmation({
            instructionConfirmation: action.instructionConfirmation,
          }),
        ),
      ),
  );

  triggerLoadChaptersWhenMyInstructionIsLoaded$ = createEffect(() =>
    this.actions.pipe(
      ofType(InstructionDetailsActions.loadedMyInstruction),
      map((action) =>
        InstructionDetailsActions.loadChapters({
          instruction: action.instruction,
        }),
      ),
    ),
  );

  triggerLoadChapterConfirmationsWhenMyInstructionIsLoaded$ = createEffect(() =>
    this.actions.pipe(
      ofType(InstructionDetailsActions.loadMyInstruction),
      map((action) =>
        InstructionDetailsActions.loadChapterConfirmations({
          instruction: action.name,
          instructionConfirmation: action.instructionConfirmation,
        }),
      ),
    ),
  );

  triggerReloadAfterChapterConfirmation$ = createEffect(() =>
    this.actions.pipe(
      ofType(
        InstructionDetailsActions.confirmedChapter,
        InstructionDetailsActions.confirmChapterFailed,
      ),
      withLatestFrom(
        this.store.select(InstructionDetailsSelectors.myInstruction),
      ),
      filter(([_, state]) => state.myInstructionConfirmation != undefined),
      map(([_, state]) =>
        InstructionDetailsActions.loadMyInstruction({
          name: state.myInstructionConfirmation!.instruction,
          instructionConfirmation: state.myInstructionConfirmation!.name,
        }),
      ),
    ),
  );

  triggerLoadQuestionsWhenChapterIsSelectedOrMyInstructionConfirmationIsLoaded$ =
    createEffect(() =>
      this.actions.pipe(
        ofType(
          InstructionDetailsActions.selectChapter,
          InstructionDetailsActions.loadedMyInstructionConfirmation,
        ),
        withLatestFrom(
          this.store.select(
            InstructionDetailsSelectors.selectedChapterAndInstructionConfirmation,
          ),
        ),
        filter(
          ([_, state]) =>
            state.myInstructionConfirmation != undefined &&
            state.selectedChapter != undefined,
        ),
        map(([_, state]) =>
          InstructionDetailsActions.loadQuestionsForChapter({
            chapter: state.selectedChapter!.name,
            instructionConfirmation: state.myInstructionConfirmation!.name,
          }),
        ),
      ),
    );

  loadMyInstructionConfirmation$ = createEffect(() =>
    this.actions.pipe(
      ofType(InstructionDetailsActions.loadMyInstructionConfirmation),
      mergeMap((action) =>
        this.instructionService
          .loadMyInstructionConfirmation(action.instructionConfirmation)
          .pipe(
            map((confirmation) =>
              InstructionDetailsActions.loadedMyInstructionConfirmation({
                confirmation,
              }),
            ),
          ),
      ),
    ),
  );

  confirmMyChapter$ = createEffect(() =>
    this.actions.pipe(
      ofType(InstructionDetailsActions.confirmChapter),
      mergeMap((action) =>
        this.chapterService
          .confirmChapter(
            action.chapter.name,
            action.instructionConfirmation.name,
            action.answers,
          )
          .pipe(
            map((confirmation) =>
              InstructionDetailsActions.confirmedChapter({
                confirmation,
              }),
            ),
            catchError((error) =>
              of(InstructionDetailsActions.confirmChapterFailed({ error })),
            ),
          ),
      ),
    ),
  );

  triggerSuccessNotifications$ = createEffect(() =>
    this.actions.pipe(
      ofType(InstructionDetailsActions.confirmedChapter),
      map((_) =>
        NotificationActions.notify({
          notification: {
            durationMs: 5000,
            notificationType: "success",
            notification: this.translate.instant(
              "instruction-details.chapter-quizz.success",
            ),
            includeConfetti: true,
          },
        }),
      ),
    ),
  );

  triggerErrorNotifications$ = createEffect(() =>
    this.actions.pipe(
      ofType(InstructionDetailsActions.confirmChapterFailed),
      map((_) =>
        NotificationActions.notify({
          notification: {
            durationMs: 10000,
            notificationType: "error",
            notification: this.translate.instant(
              "instruction-details.chapter-quizz.error",
            ),
          },
        }),
      ),
    ),
  );

  loadChapters$ = createEffect(() =>
    this.actions.pipe(
      ofType(InstructionDetailsActions.loadChapters),
      mergeMap((action) =>
        this.chapterService
          .loadChapters(action.instruction.name)
          .pipe(
            map((chapters) =>
              InstructionDetailsActions.loadedChapters({ chapters }),
            ),
          ),
      ),
    ),
  );

  loadChapterConfirmations$ = createEffect(() =>
    this.actions.pipe(
      ofType(InstructionDetailsActions.loadChapterConfirmations),
      mergeMap((action) =>
        this.chapterService
          .loadChapterConfirmations(action.instructionConfirmation)
          .pipe(
            map((chapterConfirmations) =>
              InstructionDetailsActions.loadedChapterConfirmations({
                chapterConfirmations,
              }),
            ),
          ),
      ),
    ),
  );

  loadQuestionsForChapter$ = createEffect(() =>
    this.actions.pipe(
      ofType(InstructionDetailsActions.loadQuestionsForChapter),
      mergeMap((action) =>
        this.chapterService
          .loadQuestionsForChapter(
            action.chapter,
            action.instructionConfirmation,
          )
          .pipe(
            map((questions) =>
              InstructionDetailsActions.loadedQuestionsForChapter({
                questions: questions,
              }),
            ),
          ),
      ),
    ),
  );

  constructor(
    private readonly actions: Actions,
    private readonly instructionService: InstructionService,
    private readonly chapterService: ChapterService,
    private readonly store: Store<AppState>,
    private readonly translate: TranslateService,
  ) {}
}
